/**
 * ElevationProfileWeb — altitude chart (SVG) for a Go Route on the website.
 *
 * Consumes the same `coordinates` array served by `GET /api/go-routes/{id}`
 * ({ lat, lng, alt, t }). Renders a compact filled-line chart with x-axis
 * toggle (distance vs time) and aggregated stats: gain, loss, max, min.
 *
 * Mirrors the mobile `ElevationProfile.tsx` so the UX matches across
 * platforms. Kept dependency-free (no recharts / d3) — same SVG semantics.
 */
import { useMemo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Mountain, TrendingUp, TrendingDown } from "lucide-react";
import { colorForSegment } from "./tripMap/config";

const EARTH_R_KM = 6371;
const toRad = (v) => (v * Math.PI) / 180;

function haversine(a, b) {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_R_KM * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Ignore sub-2m oscillations (GPS barometer noise) when summing gain/loss. */
function computeGainLoss(ys) {
  const THRESH = 2; // meters
  let gain = 0;
  let loss = 0;
  let last = ys[0];
  for (let i = 1; i < ys.length; i++) {
    const d = ys[i] - last;
    if (d >= THRESH) {
      gain += d;
      last = ys[i];
    } else if (d <= -THRESH) {
      loss += -d;
      last = ys[i];
    }
  }
  return { gain: Math.round(gain), loss: Math.round(loss) };
}

function buildSeries(points, axis) {
  if (!points || points.length === 0) {
    return { xs: [], ys: [], segs: [], xMin: 0, xMax: 0, yMin: 0, yMax: 0 };
  }
  const xs = [];
  const ys = [];
  const segs = [];
  const t0 = points[0].t ? new Date(points[0].t).getTime() : 0;
  // b99.10.72 (2026-05-23) — Distance-axis priority order:
  //   1. `p.distance_km` (if every point carries it) — trusts an
  //      upstream cumulative distance (e.g. the scaled value coming
  //      from `/api/routes/elevation` which factors in the Google
  //      Routes road distance, fixing the editor bug where the
  //      X-axis fechava nos ~802.7 km enquanto a etapa marcava
  //      838.2 km porque a haversine da polyline subestima rectas
  //      longas em ~4 %).
  //   2. Haversine fallback (legacy callers: mobile GPS recordings,
  //      Go Route detail page) — same behaviour as before.
  const useUpstreamKm =
    axis === "distance" &&
    points.every((p) => typeof p.distance_km === "number" && Number.isFinite(p.distance_km));
  let cumKm = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (!useUpstreamKm && i > 0) cumKm += haversine(points[i - 1], p);
    let x;
    if (axis === "distance") {
      x = useUpstreamKm ? p.distance_km : cumKm;
    } else {
      x = p.t ? Math.max(0, (new Date(p.t).getTime() - t0) / 1000) : i;
    }
    xs.push(x);
    ys.push(Number.isFinite(p.alt) ? p.alt : 0);
    // segIndex tags which trip stage this point belongs to (multi-stage
    // public/editor profiles). Absent for single-route callers (Go
    // routes, mobile recordings) → stays null → single-colour fallback.
    segs.push(Number.isFinite(p.segIndex) ? p.segIndex : null);
  }
  return {
    xs,
    ys,
    segs,
    xMin: xs[0] ?? 0,
    xMax: xs[xs.length - 1] ?? 0,
    yMin: Math.min(...ys),
    yMax: Math.max(...ys),
  };
}

/**
 * Visual gluing — `position` controls the rounded corners + border edges so
 * the profile can be sandwiched between a map (above) and a histogram
 * (below) with the three components reading as a single column.
 *   - "standalone" (default): full `rounded-xl` card.
 *   - "top":   only top corners rounded, no bottom border.
 *   - "middle": no rounded corners, no top + no bottom border (seam piece).
 *   - "bottom": only bottom corners rounded, no top border.
 *
 * Backwards-compat: when `merged` is truthy we treat it as "bottom" (the
 * previous behaviour, before a histogram was glued underneath).
 */
function classesForPosition(position) {
  switch (position) {
    case "top":
      return "rounded-t-xl border-b-0";
    case "middle":
      return "rounded-none border-y-0";
    case "bottom":
      return "rounded-b-xl border-t-0";
    default:
      return "rounded-xl";
  }
}

/**
 * Inner padding tuned per position. When stacked we want less vertical
 * padding so the perfil + histograma feel like one card (zero perceptible
 * gap). Standalone keeps the previous breathing-room feel.
 */
function paddingForPosition(position) {
  switch (position) {
    case "top":
      return "px-4 pt-4 pb-2";
    case "middle":
      return "px-4 py-2";
    case "bottom":
      return "px-4 pt-2 pb-3";
    default:
      return "p-4";
  }
}

export default function ElevationProfileWeb({
  coordinates,
  height = 130,
  onHoverPoint,
  merged = false,
  position,
}) {
  // Resolve final position once, with the legacy `merged` boolean as
  // fallback so older callers keep working.
  const { t } = useTranslation("tools");
  const resolvedPosition = position || (merged ? "bottom" : "standalone");
  const cardCorners = classesForPosition(resolvedPosition);
  const cardPadding = paddingForPosition(resolvedPosition);
  const [hover, setHover] = useState(null); // { i, px, py }
  const [width, setWidth] = useState(0);
  const containerRef = useRef(null);
  // x-axis is always distance — the "tempo" toggle was removed for clarity.
  const xAxis = "distance";

  // Check if data has meaningful altitude (all zeros = nothing to show)
  const hasAltitude = useMemo(() => {
    if (!coordinates || coordinates.length < 2) return false;
    return coordinates.some((c) => Number.isFinite(c.alt) && c.alt !== 0);
  }, [coordinates]);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Downsample to max 200 points for smooth render. Also keep the mapping
  // from downsampled index → original index so we can look up lat/lng for
  // the hover marker on the map.
  const series = useMemo(() => {
    const built = buildSeries(coordinates || [], xAxis);
    const N = 200;
    if (built.xs.length <= N) {
      return { ...built, indices: built.xs.map((_, i) => i) };
    }
    const stride = (built.xs.length - 1) / (N - 1);
    const xs = new Array(N);
    const ys = new Array(N);
    const segs = new Array(N);
    const indices = new Array(N);
    for (let i = 0; i < N; i++) {
      const idx = Math.round(i * stride);
      xs[i] = built.xs[idx];
      ys[i] = built.ys[idx];
      segs[i] = built.segs[idx];
      indices[i] = idx;
    }
    return { ...built, xs, ys, segs, indices };
  }, [coordinates, xAxis]);

  const stats = useMemo(() => {
    if (!coordinates || coordinates.length < 2) return null;
    const alts = coordinates
      .map((c) => c.alt)
      .filter((a) => Number.isFinite(a));
    if (alts.length === 0) return null;
    const { gain, loss } = computeGainLoss(alts);
    return {
      gain,
      loss,
      max: Math.round(Math.max(...alts)),
      min: Math.round(Math.min(...alts)),
    };
  }, [coordinates]);

  const padH = 36;
  const padV = 20;
  const chartW = Math.max(0, width - padH * 2);
  const chartH = Math.max(0, height - padV * 2);

  const { line, fill, pts } = useMemo(() => {
    const { xs, ys, xMin, xMax, yMin, yMax } = series;
    if (xs.length < 2 || chartW <= 0) return { line: "", fill: "", pts: [] };
    const xRange = Math.max(1e-6, xMax - xMin);
    const yRange = Math.max(10, yMax - yMin);
    const yPad = yRange * 0.08;
    const y0 = yMin - yPad;
    const y1 = yMax + yPad;
    const p = xs.map((x, i) => ({
      px: padH + ((x - xMin) / xRange) * chartW,
      py: padV + chartH - ((ys[i] - y0) / (y1 - y0)) * chartH,
    }));
    const move = `M ${p[0].px} ${p[0].py}`;
    const lineD = move + p.slice(1).map((q) => ` L ${q.px} ${q.py}`).join("");
    const fillD =
      lineD +
      ` L ${p[p.length - 1].px} ${padV + chartH} L ${p[0].px} ${padV + chartH} Z`;
    return { line: lineD, fill: fillD, pts: p };
  }, [series, chartW, chartH]);

  // Per-segment colouring: when the profile spans multiple trip stages
  // (points carry `segIndex`), draw one fill+line path per consecutive
  // run, coloured with the SAME palette as the map polylines + legend
  // (colorForSegment). Adjacent runs share their boundary point so the
  // line stays continuous. Single-route callers (no segIndex) skip this
  // and keep the original single-orange rendering.
  const hasSegments = useMemo(
    () => Array.isArray(coordinates) && coordinates.some((c) => Number.isFinite(c.segIndex)),
    [coordinates]
  );

  const coloredRuns = useMemo(() => {
    if (!hasSegments) return null;
    const { xs, ys, segs, xMin, xMax, yMin, yMax } = series;
    if (!segs || xs.length < 2 || chartW <= 0) return null;
    const xRange = Math.max(1e-6, xMax - xMin);
    const yRange = Math.max(10, yMax - yMin);
    const yPad = yRange * 0.08;
    const y0 = yMin - yPad;
    const y1 = yMax + yPad;
    const baseY = padV + chartH;
    const p = xs.map((x, i) => ({
      px: padH + ((x - xMin) / xRange) * chartW,
      py: padV + chartH - ((ys[i] - y0) / (y1 - y0)) * chartH,
    }));
    const runs = [];
    let start = 0;
    for (let i = 1; i < p.length; i++) {
      if (segs[i] !== segs[i - 1]) {
        runs.push({ from: start, to: i, seg: segs[start] }); // include boundary
        start = i;
      }
    }
    runs.push({ from: start, to: p.length - 1, seg: segs[start] });
    return runs
      .filter((r) => r.to > r.from)
      .map((r) => {
        const slice = p.slice(r.from, r.to + 1);
        const move = `M ${slice[0].px} ${slice[0].py}`;
        const lineD = move + slice.slice(1).map((q) => ` L ${q.px} ${q.py}`).join("");
        const fillD = lineD + ` L ${slice[slice.length - 1].px} ${baseY} L ${slice[0].px} ${baseY} Z`;
        return { line: lineD, fill: fillD, color: colorForSegment(r.seg || 0) };
      });
  }, [series, chartW, chartH, hasSegments]);

  const hoverColor =
    hasSegments && hover && series.segs
      ? colorForSegment(series.segs[hover.i] || 0)
      : "rgb(234 88 12)";

  const xLabel = (v) => {
    if (xAxis === "distance") return `${v.toFixed(1)} km`;
    const m = Math.floor(v / 60);
    const h = Math.floor(m / 60);
    return h > 0
      ? `${h}h${(m % 60).toString().padStart(2, "0")}`
      : `${m}min`;
  };

  const onMouseMove = (e) => {
    if (!pts.length) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < pts.length; i++) {
      const d = Math.abs(pts[i].px - mx);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    setHover({ i: best, px: pts[best].px, py: pts[best].py });
    if (onHoverPoint && series.indices && coordinates) {
      const origIdx = series.indices[best];
      const coord = coordinates[origIdx];
      if (coord) {
        onHoverPoint({
          lat: coord.lat,
          lng: coord.lng,
          alt: coord.alt,
          index: origIdx,
        });
      }
    }
  };

  const onMouseLeave = () => {
    setHover(null);
    if (onHoverPoint) onHoverPoint(null);
  };

  const hoveredAlt = hover ? Math.round(series.ys[hover.i]) : null;
  const hoveredX = hover ? series.xs[hover.i] : null;

  if (!hasAltitude) {
    return (
      <div
        className={`bg-card/80 backdrop-blur-sm border border-border ${cardCorners} ${cardPadding} text-center text-sm text-muted-foreground`}
        data-testid="elevation-profile-empty"
      >
        <Mountain className="h-5 w-5 mx-auto mb-2 text-muted-foreground/60" />
        {t("public_route.chart_no_alt_profile", "Sem dados de altitude para esta rota.")}
      </div>
    );
  }

  return (
    <div
      className={`bg-card/80 backdrop-blur-sm border border-border ${cardCorners} ${cardPadding}`}
      data-testid="elevation-profile"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Mountain className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-bold text-foreground">
            {t("public_route.chart_profile_title", "Perfil de altitude")}
          </h3>
        </div>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-4 gap-2 mb-2 text-center">
          <StatMini
            icon={TrendingUp}
            value={`+${stats.gain} m`}
            label={t("public_route.chart_gain", "Ganho")}
            positive
          />
          <StatMini
            icon={TrendingDown}
            value={`-${stats.loss} m`}
            label={t("public_route.chart_loss", "Descida")}
          />
          <StatMini value={`${stats.max} m`} label={t("public_route.chart_max", "Máx")} />
          <StatMini value={`${stats.min} m`} label={t("public_route.chart_min", "Mín")} />
        </div>
      )}

      {/* Chart */}
      <div ref={containerRef} className="relative">
        {width > 0 && series.xs.length >= 2 ? (
          <svg
            width={width}
            height={height}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            style={{ display: "block" }}
            data-testid="elevation-chart"
          >
            {/* Baseline */}
            <line
              x1={padH}
              y1={padV + chartH}
              x2={padH + chartW}
              y2={padV + chartH}
              stroke="currentColor"
              className="text-border"
              strokeWidth={1}
            />
            {/* Filled area + line — per-segment colours when the profile
                spans multiple trip stages (segIndex present), else the
                single orange used for Go routes / mobile recordings. */}
            {hasSegments && coloredRuns ? (
              coloredRuns.map((r, i) => (
                <g key={i}>
                  <path d={r.fill} fill={r.color} fillOpacity={0.18} />
                  <path d={r.line} stroke={r.color} strokeWidth={2} fill="none" />
                </g>
              ))
            ) : (
              <>
                <path d={fill} fill="rgb(234 88 12 / 0.18)" />
                <path d={line} stroke="rgb(234 88 12)" strokeWidth={2} fill="none" />
              </>
            )}
            {/* Y labels */}
            <text
              x={padH - 4}
              y={padV + 4}
              textAnchor="end"
              fontSize={10}
              className="fill-muted-foreground"
            >
              {Math.round(series.yMax)} m
            </text>
            <text
              x={padH - 4}
              y={padV + chartH}
              textAnchor="end"
              fontSize={10}
              className="fill-muted-foreground"
            >
              {Math.round(series.yMin)} m
            </text>
            {/* X labels */}
            <text
              x={padH}
              y={padV + chartH + 14}
              fontSize={10}
              className="fill-muted-foreground"
            >
              {xLabel(series.xMin)}
            </text>
            <text
              x={padH + chartW}
              y={padV + chartH + 14}
              textAnchor="end"
              fontSize={10}
              className="fill-muted-foreground"
            >
              {xLabel(series.xMax)}
            </text>
            {/* Hover marker */}
            {hover && (
              <>
                <line
                  x1={hover.px}
                  y1={padV}
                  x2={hover.px}
                  y2={padV + chartH}
                  stroke={hoverColor}
                  strokeOpacity={0.4}
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
                <circle
                  cx={hover.px}
                  cy={hover.py}
                  r={4}
                  fill={hoverColor}
                  stroke="#fff"
                  strokeWidth={1.5}
                />
              </>
            )}
          </svg>
        ) : (
          <div
            style={{ height }}
            className="flex items-center justify-center text-xs text-muted-foreground"
          >
            {t("public_route.chart_no_data", "Sem dados suficientes")}
          </div>
        )}

        {/* Hover tooltip */}
        {hover && hoveredAlt != null && (
          <div
            className="absolute pointer-events-none bg-background/95 backdrop-blur border border-border rounded-md px-2 py-1 text-xs shadow-lg"
            style={{
              left: Math.min(Math.max(hover.px - 40, 0), width - 80),
              top: Math.max(hover.py - 38, 0),
              width: 80,
              textAlign: "center",
            }}
            data-testid="elevation-tooltip"
          >
            <div className="font-bold text-primary">{hoveredAlt} m</div>
            <div className="text-[10px] text-muted-foreground">
              {xLabel(hoveredX ?? 0)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatMini({ icon: Icon, value, label, positive }) {
  return (
    <div className="bg-background/40 rounded-lg py-1.5">
      <div
        className={`text-sm font-bold flex items-center justify-center gap-1 ${
          positive ? "text-emerald-500" : "text-foreground"
        }`}
      >
        {Icon && <Icon className="h-3 w-3" />}
        {value}
      </div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
    </div>
  );
}
